import React, { useState, useEffect, useRef, useCallback } from 'react'
import { apiService } from '../../services/api'
import livekitService from '../../services/livekitService'
import VoiceIntensityVisualizer from './VoiceIntensityVisualizer'
import './PersonaLiveKitChat.css'

const PersonaLiveKitChat = ({ personaId, personaName }) => {
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [messages, setMessages] = useState([])
  const [persona, setPersona] = useState(null)
  const [error, setError] = useState(null)
  const [userSpeechBuffer, setUserSpeechBuffer] = useState('')
  const [aiSpeechBuffer, setAiSpeechBuffer] = useState('')
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  const [language, setLanguage] = useState('en-US')

  const messagesEndRef = useRef(null)
  const [welcomeMessageShown, setWelcomeMessageShown] = useState(false)

  // Load persona data
  useEffect(() => {
    const loadPersona = async () => {
      try {
        const personaData = await apiService.getPersona(personaId)
        setPersona(personaData)
      } catch (error) {
        console.error('Error loading persona:', error)
        setError('Failed to load persona data')
      }
    }

    if (personaId) {
      loadPersona()
    }
  }, [personaId])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, userSpeechBuffer, aiSpeechBuffer])

  // Generate truly unique user identity for each session
  const generateUniqueUserId = () => {
    const timestamp = Date.now()
    const randomNum = Math.floor(Math.random() * 10000)
    const sessionId = Math.random().toString(36).substring(2, 8)
    return `user_${personaId}_${timestamp}_${randomNum}_${sessionId}`
  }

  // Generate truly unique room name for each session
  const generateUniqueRoomName = () => {
    const timestamp = Date.now()
    const sessionId = Math.random().toString(36).substring(2, 8)
    return `persona_${personaId}_${timestamp}_${sessionId}`
  }

  const connectToLiveKit = async () => {
    if (isConnecting || isConnected) return

    setIsConnecting(true)
    setConnectionStatus('connecting')
    setError(null)

    try {
      // Generate unique identifiers for this session
      const uniqueUserId = generateUniqueUserId()
      const uniqueRoomName = generateUniqueRoomName()

      // Start persona voice chat using the new endpoint (similar to auth login approach)
      const response = await fetch(`${apiService.baseUrl}/api/persona/voice-chat/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          persona_id: personaId,
          user_identity: uniqueUserId,
          room_name: uniqueRoomName,
          language: language,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to start persona voice chat')
      }

      const { token, room_name, livekit_url, persona_name } = await response.json()

      // Check if LiveKit URL is properly configured
      if (!livekit_url || livekit_url === 'wss://your-livekit-server.com') {
        throw new Error('LiveKit server not configured. Please set up LiveKit server first.')
      }

      // Set up LiveKit service callbacks (same as main chat)
      livekitService.setOnConnectionStateChange((state) => {
        setConnectionStatus(state)
        if (state === 'connected') {
          setIsConnected(true)
          setIsConnecting(false)
          // Set microphone as enabled immediately (no visible toggle)
          setIsMicrophoneEnabled(true)

          // Auto-enable microphone when connected (silently)
          setTimeout(async () => {
            try {
              await livekitService.enableMicrophone()
            } catch (error) {
              console.error('Error auto-enabling microphone:', error)
              addMessage('system', 'Could not auto-enable microphone. Please click the mic button.')
            }
          }, 1000) // Small delay to ensure connection is stable

          if (!welcomeMessageShown) {
            addMessage(
              'system',
              `Connected to ${
                persona_name || persona?.name || 'your persona'
              }! You can start talking.`
            )
            setWelcomeMessageShown(true)
          }
        } else if (state === 'disconnected') {
          setIsConnected(false)
          setIsConnecting(false)
          setIsMicrophoneEnabled(false)
          addMessage('system', 'Disconnected from chat.')
        }
      })

      livekitService.setOnDataReceived((data, participant) => {
        if (data.type === 'user_speech') {
          setUserSpeechBuffer(data.text || '')
        } else if (data.type === 'ai_speech') {
          setAiSpeechBuffer(data.text || '')
        } else if (data.type === 'final_user_speech') {
          setUserSpeechBuffer('')
          addMessage('user', data.text)
        } else if (data.type === 'final_ai_speech') {
          setAiSpeechBuffer('')
          addMessage('persona', data.text)
        }
      })

      livekitService.setOnTrackSubscribed((track, publication, participant) => {
        if (track.kind === 'audio') {
          const audioElement = track.attach()
          audioElement.play()
        }
      })

      // Connect to LiveKit room
      await livekitService.connect(livekit_url, token, room_name)
    } catch (error) {
      console.error('Error connecting to LiveKit:', error)
      setError(`Failed to connect: ${error.message}`)
      setIsConnecting(false)
      setConnectionStatus('disconnected')
    }
  }

  const disconnectFromLiveKit = useCallback(async () => {
    try {
      // Stop persona voice chat using the new endpoint
      const response = await fetch(
        `${apiService.baseUrl}/api/persona/voice-chat/stop/${personaId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        console.warn('Failed to stop persona voice chat on server')
      }

      // Disconnect from LiveKit
      await livekitService.disconnect()
      setIsConnected(false)
      setIsMicrophoneEnabled(false)
      setConnectionStatus('disconnected')
      addMessage('system', 'Disconnected from persona chat.')
    } catch (error) {
      console.error('Error disconnecting:', error)
    }
  }, [personaId])

  // Handle browser tab/window close to ensure clean disconnect
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (isConnected) {
        // Try to disconnect gracefully
        try {
          await disconnectFromLiveKit()
        } catch (error) {
          console.warn('Could not disconnect persona chat gracefully on page unload:', error)
        }
      }
    }

    const handleVisibilityChange = () => {
      // When tab becomes hidden for extended time, disconnect
      if (document.hidden && isConnected) {
        setTimeout(() => {
          if (document.hidden && isConnected) {
            console.log('Persona chat tab hidden for extended time, disconnecting...')
            disconnectFromLiveKit()
          }
        }, 60000) // 60 seconds for persona chat
      }
    }

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isConnected, disconnectFromLiveKit])

  const addMessage = (type, content) => {
    const message = {
      id: Date.now(),
      type,
      content,
      timestamp: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, message])
  }

  const toggleMicrophone = async () => {
    if (!isConnected) return

    try {
      if (isMicrophoneEnabled) {
        await livekitService.disableMicrophone()
        setIsMicrophoneEnabled(false)
        addMessage('system', 'Microphone disabled.')
      } else {
        const micEnabled = await livekitService.enableMicrophone()
        if (micEnabled) {
          setIsMicrophoneEnabled(true)
          addMessage('system', 'Microphone enabled! You can now speak to the persona.')
        } else {
          addMessage('system', 'Microphone access denied. Please check your browser permissions.')
        }
      }
    } catch (error) {
      console.error('Error toggling microphone:', error)
      addMessage('system', 'Error with microphone. Please try again.')
    }
  }

  if (error) {
    return (
      <div className="persona-chat-error">
        <h2>LiveKit Connection Error</h2>
        <p>{error}</p>
        <div className="error-actions">
          <button onClick={() => window.location.reload()}>Retry</button>
          <button onClick={() => (window.location.href = '/')}>Use Regular Chat Instead</button>
        </div>
        <div className="error-info">
          <h3>LiveKit Setup Required</h3>
          <p>To use voice chat, you need to set up a LiveKit server:</p>
          <ol>
            <li>Deploy a LiveKit server (Docker, cloud, or self-hosted)</li>
            <li>Set environment variables: LIVEKIT_API_KEY, LIVEKIT_API_SECRET, LIVEKIT_URL</li>
            <li>Restart the backend server</li>
          </ol>
        </div>
      </div>
    )
  }

  if (!persona) {
    return (
      <div className="persona-chat-loading">
        <div className="loading-spinner"></div>
        <p>Loading persona...</p>
      </div>
    )
  }

  return (
    <div className="persona-livekit-chat">
      {/* Connecting Overlay */}
      {isConnecting && (
        <div className="connecting-overlay">
          <div className="loading-spinner"></div>
          <div className="connecting-text">Connecting to {persona.name}...</div>
        </div>
      )}

      <div className="chat-main-area">
        <div className="persona-profile">
          <div className="persona-avatar-large">{persona.name.charAt(0).toUpperCase()}</div>
          <div className="persona-name">{persona.name}</div>
          <div className="persona-status-indicator">
            <span className={`status-dot ${connectionStatus}`}></span>
            {connectionStatus === 'connected' && 'Ready to chat'}
            {connectionStatus === 'connecting' && 'Connecting...'}
            {connectionStatus === 'disconnected' && 'Disconnected'}
            {connectionStatus === 'error' && 'Error'}
          </div>
        </div>

        {/* Speech buffers for real-time feedback */}
        {(userSpeechBuffer || aiSpeechBuffer) && (
          <div className="speech-feedback">
            {userSpeechBuffer && (
              <div className="speech-bubble user-speech">
                <div className="speech-text">You: {userSpeechBuffer}</div>
              </div>
            )}
            {aiSpeechBuffer && (
              <div className="speech-bubble ai-speech">
                <div className="speech-text">
                  {persona.name}: {aiSpeechBuffer}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Voice Intensity Visualizers - Center of Screen */}
      <div className="center-voice-visualizers">
        <VoiceIntensityVisualizer
          isActive={isConnected && isMicrophoneEnabled}
          type="user"
          barCount={5}
          minHeight={12}
          className="user-voice-viz"
        />
        <VoiceIntensityVisualizer
          isActive={isConnected}
          type="ai"
          barCount={5}
          minHeight={12}
          className="ai-voice-viz"
        />
      </div>

      <div className="voice-controls">
        <div className="control-buttons-container">
          {!isConnected ? (
            <div className="bottom-controls-pill">
              <div className="language-selector">
                <label htmlFor="language-select">Language:</label>
                <select
                  id="language-select"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={isConnected}
                >
                  <option value="en-US">English (US)</option>
                  <option value="en-GB">English (UK)</option>
                  <option value="es-ES">Spanish (Spain)</option>
                  <option value="es-MX">Spanish (Mexico)</option>
                  <option value="fr-FR">French</option>
                  <option value="de-DE">German</option>
                  <option value="it-IT">Italian</option>
                  <option value="pt-BR">Portuguese (Brazil)</option>
                  <option value="zh-CN">Chinese (Simplified)</option>
                  <option value="zh-TW">Chinese (Traditional)</option>
                  <option value="ja-JP">Japanese</option>
                  <option value="ko-KR">Korean</option>
                  <option value="ru-RU">Russian</option>
                  <option value="ar-SA">Arabic</option>
                  <option value="hi-IN">Hindi</option>
                  <option value="th-TH">Thai</option>
                  <option value="vi-VN">Vietnamese</option>
                  <option value="nl-NL">Dutch</option>
                  <option value="sv-SE">Swedish</option>
                  <option value="da-DK">Danish</option>
                  <option value="no-NO">Norwegian</option>
                  <option value="fi-FI">Finnish</option>
                  <option value="pl-PL">Polish</option>
                  <option value="tr-TR">Turkish</option>
                  <option value="he-IL">Hebrew</option>
                </select>
              </div>

              <button
                onClick={connectToLiveKit}
                disabled={isConnecting}
                className="connect-btn"
                title="Connect to start voice chat"
              >
                <div className="control-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                  </svg>
                </div>
                {isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          ) : (
            <>
              <div className="mute-control">
                <button
                  onClick={toggleMicrophone}
                  className={`btn-control mute-btn ${isMicrophoneEnabled ? 'enabled' : 'disabled'}`}
                  title={isMicrophoneEnabled ? 'Click to mute' : 'Click to unmute'}
                >
                  <div className="control-icon">
                    {isMicrophoneEnabled ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                        <path d="M1.39 4.22L2.8 2.81l18.38 18.38-1.41 1.41L1.39 4.22z" />
                      </svg>
                    )}
                  </div>
                </button>
                <div className="control-status">{isMicrophoneEnabled ? 'Mute' : 'Unmute'}</div>
              </div>

              <div className="disconnect-control">
                <button
                  onClick={disconnectFromLiveKit}
                  className="btn-control disconnect-btn"
                  title="End call"
                >
                  <div className="control-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.73-1.68-1.36-2.66-1.85-.33-.16-.56-.51-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
                    </svg>
                  </div>
                </button>
                <div className="control-status">End Call</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PersonaLiveKitChat
